import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from '../store/languageStore';
import validationService from '../services/validationService';
import { brand, radii } from '../theme';

interface AtlasField {
  path: string;
  label: string;
  required: boolean;
  section: 'kopf' | 'ausfuhrer' | 'empfaenger' | 'anmelder' | 'position';
}

interface ValidationIssue {
  type: 'error' | 'warning';
  field: string;
  message: string;
}

interface AtlasValidationPreviewProps {
  extractedData: any;
  selectedModule: string;
}

// Base ATLAS fields (without position items) - includes ALL BEO required fields
// Updated based on successful submission sample from BEO ATLAS API
const BASE_ATLAS_FIELDS: AtlasField[] = [
  // ===== KOPF (Header) - Required fields from BEO ATLAS schema =====
  { path: 'kopf.eoriNiederlassungsnummer', label: 'EORI Branch Number', required: true, section: 'kopf' },
  { path: 'kopf.dienststellennummer', label: 'Customs Office Number', required: true, section: 'empfaenger' },
  { path: 'kopf.lrn', label: 'Invoice Number (LRN)', required: true, section: 'kopf' },
  { path: 'kopf.artderAnmeldung', label: 'Type of Registration', required: true, section: 'kopf' },
  { path: 'kopf.artderAusfuhranmeldung', label: 'Export Declaration Type', required: true, section: 'kopf' },
  { path: 'kopf.beteiligtenKonstellation', label: 'Participant Constellation', required: true, section: 'kopf' },
  { path: 'kopf.sicherheit', label: 'Security Indicator', required: true, section: 'kopf' },
  { path: 'kopf.besondereUmstande', label: 'Special Circumstances', required: true, section: 'kopf' },
  { path: 'kopf.inRechnunggestellterGesamtbetrag', label: 'Total Invoice Amount', required: true, section: 'kopf' },
  { path: 'kopf.rechnungswahrung', label: 'Invoice Currency', required: true, section: 'kopf' },
  { path: 'kopf.ausfuhrzollstelleDienststellennummer', label: 'Export Customs Office', required: true, section: 'kopf' },
  { path: 'kopf.vorgeseheneAusgangszollstelleDienststellennummer', label: 'Intended Exit Customs Office', required: true, section: 'kopf' },
  { path: 'kopf.artdesGeschafts', label: 'Type of Transaction', required: true, section: 'kopf' },
  { path: 'kopf.angabenGeschaftsvorgangArt', label: 'Nature of Transaction', required: true, section: 'kopf' },
  { path: 'kopf.ausfuhrland', label: 'Export Country', required: true, section: 'kopf' },
  { path: 'kopf.bestimmungsLand', label: 'Destination Country', required: true, section: 'kopf' },
  { path: 'kopf.lieferbedingungIncotermCode', label: 'Incoterm Code', required: true, section: 'kopf' },
  { path: 'kopf.lieferbedingungOrt', label: 'Delivery Place', required: true, section: 'kopf' },
  { path: 'kopf.lieferbedingungLand', label: 'Delivery Country', required: true, section: 'kopf' },
  { path: 'kopf.containerIndikator', label: 'Container Indicator', required: true, section: 'kopf' },
  { path: 'kopf.inlandischerVerkehrszweig', label: 'Domestic Transport Mode', required: true, section: 'kopf' },
  { path: 'kopf.verkehrszweigAnDerGrenze', label: 'Border Transport Mode', required: true, section: 'kopf' },
  { path: 'kopf.gesamtRohmasse', label: 'Total Gross Weight (kg)', required: true, section: 'kopf' },
  // Optional header fields
  { path: 'kopf.zeitpunktderAnmeldung', label: 'Declaration Timestamp', required: false, section: 'kopf' },
  { path: 'kopf.massgeblichesDatum', label: 'Relevant Date', required: false, section: 'kopf' },
  { path: 'kopf.kopfDatumdesAusgangs', label: 'Date of Exit', required: false, section: 'kopf' },
  { path: 'kopf.zeitpunktDerGestellung', label: 'Presentation Timestamp', required: false, section: 'kopf' },
  { path: 'kopf.zeitpunktdesEndesderLadetatigkeit', label: 'End of Loading Timestamp', required: false, section: 'kopf' },
  { path: 'kopf.gestellungszollstelle', label: 'Customs Office of Presentation', required: false, section: 'kopf' },
  { path: 'kopf.ausfuhrZollstelleFurDieErganzende', label: 'Supplementary Export Customs Office', required: false, section: 'kopf' },
  { path: 'kopf.anzahlPositionen', label: 'Number of Positions', required: false, section: 'kopf' },
  { path: 'kopf.bezugsnummer', label: 'Reference Number', required: false, section: 'kopf' },
  { path: 'kopf.datum', label: 'Invoice Date', required: false, section: 'kopf' },
  { path: 'kopf.lieferbedingungUnLocode', label: 'Delivery UN/LOCODE', required: false, section: 'kopf' },
  { path: 'kopf.lieferbedingungText', label: 'Delivery Terms Text', required: false, section: 'kopf' },
  { path: 'kopf.ladeort.code', label: 'Loading Place Code', required: false, section: 'kopf' },
  { path: 'kopf.beforderungskostenZahlungsart', label: 'Transport Cost Payment Method', required: false, section: 'kopf' },

  // ===== BEWILLIGUNG (Authorization) - Array in kopf =====
  { path: 'kopf.bewilligung[0].art', label: 'Authorization Type', required: true, section: 'kopf' },
  { path: 'kopf.bewilligung[0].referenznummer', label: 'Authorization Reference Number', required: true, section: 'kopf' },

  // ===== ANMELDER (Declarant) - Required per sample =====
  { path: 'kopf.anmelder.tin', label: 'Declarant EORI/TIN', required: true, section: 'anmelder' },
  { path: 'kopf.anmelder.niederlassungsNummer', label: 'Declarant Branch Number', required: true, section: 'anmelder' },
  { path: 'kopf.anmelder.phone', label: 'Declarant Phone', required: true, section: 'anmelder' },
  { path: 'kopf.anmelder.ansprechName', label: 'Declarant Contact Person', required: true, section: 'anmelder' },
  { path: 'kopf.anmelder.beauftragterName', label: 'Declarant Authorized Name', required: false, section: 'anmelder' },
  { path: 'kopf.anmelder.identifikationsart', label: 'Declarant Identification Type', required: false, section: 'anmelder' },

  // ===== VERTRETER (Representative) / SUBUNTERNEHMER (Subcontractor) =====
  //
  // Both parties are OPTIONAL. They were marked required "per sample" — copied
  // from one filled-in example rather than from a rule — so a declaration filed
  // WITHOUT a customs representative showed five red "Required field - must be
  // filled" markers while the Errors tab read 0. Same fault as the unterlage
  // group below: the form demanded what ATLAS does not.
  //
  // MEASURED: with vertreter and subunternehmer both empty, the validator
  // returns no error for either — 15 errors with the block present and empty,
  // 15 with it absent entirely. atlas_defaults says the same in words, seeding
  // them "" with the comment "To be configured if using customs representative"
  // / "if using subcontractor". You only name a representative if you use one,
  // and demanding one asks the user to invent a party.
  //
  // Once a representative IS named the group's own fields become required, and
  // that is a conditional rule on a populated group: fieldRuleStatuses
  // overrides this static flag, and ATLAS enforces it regardless.
  { path: 'kopf.vertreter.tin', label: 'Representative EORI/TIN', required: false, section: 'anmelder' },
  { path: 'kopf.vertreter.niederlassungsNummer', label: 'Representative Branch Number', required: false, section: 'anmelder' },
  { path: 'kopf.vertreter.phone', label: 'Representative Phone', required: false, section: 'anmelder' },
  { path: 'kopf.vertreter.ansprechName', label: 'Representative Contact Person', required: false, section: 'anmelder' },
  { path: 'kopf.subunternehmer.tin', label: 'Subcontractor EORI/TIN', required: false, section: 'anmelder' },
  { path: 'kopf.subunternehmer.niederlassungsNummer', label: 'Subcontractor Branch Number', required: false, section: 'anmelder' },

  // ===== EMPFANGER (Recipient) - Required fields INSIDE KOPF =====
  { path: 'kopf.empfanger.name', label: 'Recipient Name', required: true, section: 'empfaenger' },
  { path: 'kopf.empfanger.strasse', label: 'Recipient Street', required: true, section: 'empfaenger' },
  { path: 'kopf.empfanger.plz', label: 'Recipient Postal Code', required: true, section: 'empfaenger' },
  { path: 'kopf.empfanger.ort', label: 'Recipient City', required: true, section: 'empfaenger' },
  { path: 'kopf.empfanger.land', label: 'Recipient Country', required: true, section: 'empfaenger' },
  { path: 'kopf.empfanger.tin', label: 'Recipient TIN/EORI', required: false, section: 'empfaenger' },
  { path: 'kopf.empfanger.niederlassungsNummer', label: 'Recipient Branch Number', required: false, section: 'empfaenger' },
  { path: 'kopf.empfanger.identifikationsart', label: 'Recipient Identification Type', required: false, section: 'empfaenger' },

  // ===== AUSFUHRER (Exporter) - INSIDE KOPF per BEO ATLAS spec =====
  { path: 'kopf.ausfuhrer.name', label: 'Exporter Name', required: true, section: 'ausfuhrer' },
  { path: 'kopf.ausfuhrer.strasse', label: 'Exporter Street', required: true, section: 'ausfuhrer' },
  { path: 'kopf.ausfuhrer.plz', label: 'Exporter Postal Code', required: true, section: 'ausfuhrer' },
  { path: 'kopf.ausfuhrer.ort', label: 'Exporter City', required: true, section: 'ausfuhrer' },
  { path: 'kopf.ausfuhrer.land', label: 'Exporter Country', required: true, section: 'ausfuhrer' },
  { path: 'kopf.ausfuhrer.tin', label: 'Exporter EORI/TIN', required: true, section: 'ausfuhrer' },

  // ===== WARENORT (Location of Goods) - Required per sample =====
  { path: 'kopf.warenortArtdesOrtes', label: 'Location Type', required: true, section: 'kopf' },
  { path: 'kopf.warenortArtDerOrtsbestimmung', label: 'Location Determination Type', required: true, section: 'kopf' },
  { path: 'kopf.warenortZusatzlicheKennung', label: 'Additional Location Identifier', required: true, section: 'kopf' },
  { path: 'kopf.warenortBewilligungsnummer', label: 'Location Authorization Number', required: false, section: 'kopf' },

  // ===== BEFORDERUNGSMITTEL BEIM ABGANG (Transport at Departure) - Array =====
  { path: 'kopf.beforderungsmittelBeimAbgang[0].artderIdentifikation', label: 'Transport ID Type at Departure', required: true, section: 'kopf' },
  { path: 'kopf.beforderungsmittelBeimAbgang[0].kennzeichen', label: 'Vehicle Registration at Departure', required: true, section: 'kopf' },
  { path: 'kopf.beforderungsmittelBeimAbgang[0].staatszugehorigkeit', label: 'Vehicle Nationality at Departure', required: true, section: 'kopf' },

  // ===== BEFORDERUNGSROUTE (Transport Route) =====
  { path: 'kopf.beforderungsroute.ausgewahlteLander', label: 'Transit Countries', required: false, section: 'kopf' },

  // ===== BEFORDERUNGSMITTEL DER GRENZE (Border Transport) =====
  { path: 'kopf.beforderungsmittelderGrenzeArt', label: 'Border Transport Mode Type', required: true, section: 'kopf' },
  { path: 'kopf.beforderungsmittelderGrenzeKennzeichen', label: 'Border Vehicle Registration', required: true, section: 'kopf' },
  { path: 'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit', label: 'Border Vehicle Nationality', required: true, section: 'kopf' },
];

// Helper function to generate position fields for a specific index
// Updated to match ATLAS official field names from BEO documentation and successful sample
const getPositionFieldsForIndex = (index: number, showLabel: boolean): AtlasField[] => {
  const itemLabel = showLabel ? ` (Item ${index + 1})` : '';
  return [
    // ===== Required position fields from BEO ATLAS schema =====
    { path: `position[${index}].warePositionsnummer`, label: `Position Number${itemLabel}`, required: true, section: 'position' },
    { path: `position[${index}].beantragtesVerfahren`, label: `Requested Procedure${itemLabel}`, required: true, section: 'position' },
    { path: `position[${index}].vorhergehendesVerfahren`, label: `Previous Procedure${itemLabel}`, required: true, section: 'position' },
    { path: `position[${index}].ursprungsland`, label: `Country of Origin${itemLabel}`, required: true, section: 'position' },
    { path: `position[${index}].ursprungsVersendungsregion`, label: `Region of Origin${itemLabel}`, required: true, section: 'position' },
    { path: `position[${index}].wareWarenbezeichnung`, label: `Product Description${itemLabel}`, required: true, section: 'position' },
    { path: `position[${index}].wareWarennummerKN8`, label: `HS/CN Code${itemLabel}`, required: true, section: 'position' },
    { path: `position[${index}].wareRohmasse`, label: `Gross Weight (kg)${itemLabel}`, required: true, section: 'position' },
    { path: `position[${index}].wareEigenmasse`, label: `Net Weight (kg)${itemLabel}`, required: true, section: 'position' },
    { path: `position[${index}].mengeinbesondererMabeinheit`, label: `Quantity in Special Unit${itemLabel}`, required: true, section: 'position' },

    // ===== VERPACKUNG (Packaging) - Array within position =====
    { path: `position[${index}].verpackung[0].artderVerpackung`, label: `Package Type${itemLabel}`, required: true, section: 'position' },
    { path: `position[${index}].verpackung[0].anzahlderPackstucke`, label: `Number of Packages${itemLabel}`, required: true, section: 'position' },
    { path: `position[${index}].verpackung[0].versandzeichen`, label: `Shipping Marks${itemLabel}`, required: true, section: 'position' },
    { path: `position[${index}].verpackung[0].packstuckverweisPositionsnummer`, label: `Package Reference Position${itemLabel}`, required: false, section: 'position' },

    // ===== UNTERLAGE (Document) - Array within position =====
    //
    // A supporting document is OPTIONAL. These were marked required on every
    // line item, so an 11-item declaration showed 11 red "Document Type -
    // Required field - must be filled" markers while the Errors tab read 0 —
    // ATLAS was perfectly happy and the form was not. Supporting documents are
    // licences, permits and certificates; most commercial consignments carry
    // none, and demanding one per item asks the user to invent paperwork.
    //
    // The type IS required once an entry EXISTS, and ATLAS enforces that
    // itself: an entry with a reference and no code came back as "Unterlage
    // Unterlagetyp benoetigt a Qualifikator". That is a conditional rule on a
    // populated group, not a per-item obligation.
    { path: `position[${index}].unterlage[0].art`, label: `Document Type${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].unterlage[0].referenznummer`, label: `Document Reference${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].unterlage[0].qualifikator`, label: `Document Qualifier${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].unterlage[0].zeilenPositionsnummer`, label: `Document Line Position${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].unterlage[0].zusatzlicheAngaben`, label: `Additional Document Info${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].unterlage[0].detail`, label: `Document Detail${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].unterlage[0].namederausstellendenBehorde`, label: `Issuing Authority${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].unterlage[0].datumderAusstellung`, label: `Issue Date${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].unterlage[0].gultigkeitsdatum`, label: `Validity Date${itemLabel}`, required: false, section: 'position' },

    // ===== SONSTIGER VERWEIS (Other Reference) - Array within position =====
    // Optional, like unterlage above: its own reference number is already
    // required: false, so marking the TYPE required demanded a code for a
    // group the user never filled. Required only once an entry exists, which
    // ATLAS checks itself.
    { path: `position[${index}].sonstigerVerweis[0].art`, label: `Other Reference Type${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].sonstigerVerweis[0].referenznummer`, label: `Other Reference Number${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].sonstigerVerweis[0].qualifikator`, label: `Other Reference Qualifier${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].sonstigerVerweis[0].detail`, label: `Other Reference Detail${itemLabel}`, required: false, section: 'position' },

    // ===== Optional position fields =====
    { path: `position[${index}].statistischerWert`, label: `Statistical Value${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].artdesGeschafts`, label: `Type of Transaction${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].ausfuhrLand`, label: `Export Country${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].referenznummerUCR`, label: `UCR Reference${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].registriernummerextern`, label: `External Registration Number${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].zusatzlichesVerfahren`, label: `Additional Procedure${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].warecusnummer`, label: `CUS Number${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].menge`, label: `Quantity${itemLabel}`, required: false, section: 'position' },
    { path: `position[${index}].userid`, label: `User ID${itemLabel} (System)`, required: false, section: 'position' },
  ];
};

// Dynamic field generation function that handles multiple line items with edge case handling
export const getATLASFields = (extractedData: any): AtlasField[] => {
  // Handle all edge cases for position array
  const positions = extractedData?.position;

  // Edge case 1: positions is null, undefined, or not an array
  if (!positions || !Array.isArray(positions)) {
    // Return base fields + single item without label
    return [...BASE_ATLAS_FIELDS, ...getPositionFieldsForIndex(0, false)];
  }

  // Edge case 2: Filter out null/undefined items and get actual valid indices
  // This handles sparse arrays like [item, null, item] → indices [0, 2]
  const validIndices = positions
    .map((item, idx) => (item && typeof item === 'object') ? idx : null)
    .filter((idx): idx is number => idx !== null);

  // Edge case 3: Empty array or all items are null
  if (validIndices.length === 0) {
    // Return base fields + single item without label
    return [...BASE_ATLAS_FIELDS, ...getPositionFieldsForIndex(0, false)];
  }

  // Generate fields for each VALID index only
  const positionFields: AtlasField[] = [];
  const hasMultiple = validIndices.length > 1;

  validIndices.forEach(i => {
    positionFields.push(...getPositionFieldsForIndex(i, hasMultiple));
  });

  return [...BASE_ATLAS_FIELDS, ...positionFields];
};

// Export static ATLAS_FIELDS for backward compatibility (with 1 item minimum)
export const ATLAS_FIELDS: AtlasField[] = getATLASFields({});

// Fields that need manual verification even if extracted
export const VERIFICATION_REQUIRED_FIELDS = [
  'kopf.empfanger.name',
  'kopf.empfanger.strasse',
  'kopf.empfanger.plz',
  'kopf.empfanger.ort',
  'kopf.empfanger.land',
  'position[0].wareWarennummerKN8',
  'kopf.bestimmungsLand',  // Fixed: now directly in kopf
];

// ============================================================================
// Path-to-Label Mapping for consistent field display across components
// ============================================================================

// Header field paths to readable labels
export const ATLAS_PATH_TO_LABEL: Record<string, string> = {
  // Kopf (Declaration Header)
  'kopf.lrn': 'Invoice Number (LRN)',
  'kopf.artderAnmeldung': 'Type of Registration',
  'kopf.artderAusfuhranmeldung': 'Export Declaration Type',
  'kopf.beteiligtenKonstellation': 'Participant Constellation',
  'kopf.zeitpunktderAnmeldung': 'Declaration Timestamp',
  'kopf.sicherheit': 'Security Indicator',
  'kopf.gestellungszollstelle': 'Customs Office of Presentation',
  'kopf.ausfuhrzollstelleDienststellennummer': 'Export Customs Office',
  'kopf.inRechnunggestellterGesamtbetrag': 'Total Invoice Amount',
  'kopf.rechnungswahrung': 'Invoice Currency',
  'kopf.datum': 'Invoice Date',

  // Exporter fields
  'kopf.ausfuhrer.name': 'Exporter Name',
  'kopf.ausfuhrer.strasse': 'Exporter Street',
  'kopf.ausfuhrer.plz': 'Exporter Postal Code',
  'kopf.ausfuhrer.ort': 'Exporter City',
  'kopf.ausfuhrer.land': 'Exporter Country',
  'kopf.ausfuhrer.tin': 'Exporter EORI/TIN',

  // Recipient fields
  'kopf.empfanger.name': 'Recipient Name',
  'kopf.empfanger.strasse': 'Recipient Street',
  'kopf.empfanger.plz': 'Recipient Postal Code',
  'kopf.empfanger.ort': 'Recipient City',
  'kopf.empfanger.land': 'Recipient Country',
  'kopf.empfanger.identifikationsart': 'Recipient ID Type',

  // Declarant fields
  'kopf.anmelder.tin': 'Declarant EORI/TIN',
  'kopf.anmelder.beauftragterName': 'Declarant Authorized Name',
  'kopf.anmelder.ansprechpartnerName': 'Declarant Contact Person',
  'kopf.anmelder.phone': 'Declarant Phone',
  'kopf.anmelder.niederlassungsNummer': 'Declarant Branch Number',
  'kopf.anmelder.identifikationsart': 'Declarant ID Type',

  // Declaration Details
  'kopf.containerIndikator': 'Container Indicator',
  'kopf.anzahlPositionen': 'Number of Positions',
  'kopf.bezugsnummer': 'Reference Number',
  'kopf.eoriNiederlassungsnummer': 'EORI Branch Number',

  // Customs/Transport
  'kopf.vorgeseheneAusgangszollstelleDienststellennummer': 'Exit Customs Office',
  'kopf.ladeort.code': 'Loading Place Code',
  'kopf.beforderungsmittelderGrenzeArt': 'Border Transport Type',
  'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit': 'Border Transport Nationality',
  'kopf.ausfuhrland': 'Export Country',
  'kopf.bestimmungsLand': 'Destination Country',
  'kopf.lieferbedingungIncotermCode': 'Incoterm Code',
  'kopf.lieferbedingungOrt': 'Delivery Place',
  'kopf.gesamtRohmasse': 'Total Gross Weight (kg)',
  'kopf.inlandischerVerkehrszweig': 'Domestic Transport Mode',
  'kopf.verkehrszweigAnDerGrenze': 'Border Transport Mode',

  // Additional kopf fields
  'kopf.massgeblichesDatum': 'Relevant Date',
  'kopf.dienststellennummer': 'Customs Office Number',
  'kopf.besondereUmstande': 'Special Circumstances',
  'kopf.ausgangszollstelle': 'Exit Customs Office',
  'kopf.eingangszollstelle': 'Entry Customs Office',
  'kopf.verfahren': 'Procedure',
  'kopf.anmeldeart': 'Declaration Type Code',
  'kopf.artdesGeschafts': 'Type of Transaction',
  'kopf.angabenGeschaftsvorgangArt': 'Nature of Transaction',
  'kopf.beforderungskostenZahlungsart': 'Transport Payment Method',
  'kopf.verkehrszweig': 'Transport Mode',
  'kopf.messages': 'Validation Messages',

  // Bewilligung (Authorization) fields - both with and without kopf prefix
  'kopf.bewilligung[0].art': 'Authorization Type',
  'kopf.bewilligung[0].referenznummer': 'Authorization Reference Number',
  'bewilligung.art': 'Authorization Type',
  'bewilligung.referenznummer': 'Authorization Reference',

  // Vertreter (Representative) fields
  'kopf.vertreter.tin': 'Representative EORI/TIN',
  'kopf.vertreter.niederlassungsNummer': 'Representative Branch Number',
  'kopf.vertreter.phone': 'Representative Phone',
  'kopf.vertreter.ansprechName': 'Representative Contact Person',
  'vertreter.tin': 'Representative EORI/TIN',
  'vertreter.niederlassungsNummer': 'Representative Branch Number',
  'vertreter.phone': 'Representative Phone',
  'vertreter.ansprechName': 'Representative Contact Person',

  // Subunternehmer (Subcontractor) fields
  'kopf.subunternehmer.tin': 'Subcontractor EORI/TIN',
  'kopf.subunternehmer.niederlassungsNummer': 'Subcontractor Branch Number',
  'subunternehmer.tin': 'Subcontractor EORI/TIN',
  'subunternehmer.niederlassungsNummer': 'Subcontractor Branch Number',

  // Warenort (Location of Goods) fields
  'kopf.warenortArtdesOrtes': 'Location Type',
  'kopf.warenortArtDerOrtsbestimmung': 'Location Determination Type',
  'kopf.warenortZusatzlicheKennung': 'Additional Location Identifier',
  'kopf.warenortBewilligungsnummer': 'Location Authorization Number',

  // Beforderungsmittel beim Abgang (Transport at Departure)
  'kopf.beforderungsmittelBeimAbgang[0].artderIdentifikation': 'Transport ID Type at Departure',
  'kopf.beforderungsmittelBeimAbgang[0].kennzeichen': 'Vehicle Registration at Departure',
  'kopf.beforderungsmittelBeimAbgang[0].staatszugehorigkeit': 'Vehicle Nationality at Departure',

  // Beforderungsroute (Transport Route)
  'kopf.beforderungsroute.ausgewahlteLander': 'Transit Countries',

  // Beforderungsmittel der Grenze (Border Transport)
  'kopf.beforderungsmittelderGrenzeKennzeichen': 'Border Vehicle Registration',

  // Additional header fields from sample
  'kopf.ausfuhrZollstelleFurDieErganzende': 'Supplementary Export Customs Office',
  'kopf.kopfDatumdesAusgangs': 'Date of Exit',
  'kopf.zeitpunktDerGestellung': 'Presentation Timestamp',
  'kopf.zeitpunktdesEndesderLadetatigkeit': 'End of Loading Timestamp',
  'kopf.lieferbedingungLand': 'Delivery Country',
  'kopf.lieferbedingungUnLocode': 'Delivery UN/LOCODE',
  'kopf.lieferbedingungText': 'Delivery Terms Text',

  // Anmelder additional field
  'kopf.anmelder.ansprechName': 'Declarant Contact Person',

  // Exporter contact fields (kopf.ausfuhrer.*)
  'kopf.ausfuhrer.telefonnummer': 'Exporter Phone',
  'kopf.ausfuhrer.fax': 'Exporter Fax',
  'kopf.ausfuhrer.email': 'Exporter Email',
  'kopf.ausfuhrer.bundesland': 'Exporter Federal State',

  // Ausfuhrer (without kopf prefix - for flattened data)
  'ausfuhrer.name': 'Exporter Name',
  'ausfuhrer.strasse': 'Exporter Street',
  'ausfuhrer.plz': 'Exporter Postal Code',
  'ausfuhrer.ort': 'Exporter City',
  'ausfuhrer.land': 'Exporter Country',
  'ausfuhrer.tin': 'Exporter EORI/TIN',
  'ausfuhrer.telefonnummer': 'Exporter Phone',
  'ausfuhrer.fax': 'Exporter Fax',
  'ausfuhrer.email': 'Exporter Email',
  'ausfuhrer.bundesland': 'Exporter Federal State',

  // Empfanger (without kopf prefix - for flattened data)
  'empfanger.name': 'Recipient Name',
  'empfanger.strasse': 'Recipient Street',
  'empfanger.plz': 'Recipient Postal Code',
  'empfanger.ort': 'Recipient City',
  'empfanger.land': 'Recipient Country',
  'empfanger.tin': 'Recipient EORI/TIN',
  'empfanger.bundesland': 'Recipient Federal State',

  // Lieferung (without kopf prefix - for flattened data)
  'lieferung.lieferbedingungen': 'Delivery Terms',
  'lieferung.ort': 'Delivery Location',
  'lieferung.bestimmungsLand': 'Destination Country',
  'lieferung.verkaufLand': 'Sales Country',
  'lieferung.lieferLand': 'Delivery Country',
  'lieferung.kosten': 'Delivery Costs',
  'lieferung.wahrung': 'Currency',

  // Sendung (without kopf prefix - for flattened data)
  'sendung.beforderungsmittel': 'Transport Means',
  'sendung.beforderungsroute': 'Transport Route',
  'sendung.containerNummer': 'Container Number',
  'sendung.versandzeichen': 'Shipping Marks',
  'sendung.gesamtRohmasse': 'Total Gross Weight',
  'sendung.anzahlPackstucke': 'Number of Packages',
};

// Position field base names (without index) - Comprehensive list
export const POSITION_FIELD_LABELS: Record<string, string> = {
  // Core position fields
  'warePositionsnummer': 'Position Number',
  'beantragtesVerfahren': 'Requested Procedure',
  'vorhergehendesVerfahren': 'Previous Procedure',
  'ursprungsland': 'Country of Origin',
  'wareWarenbezeichnung': 'Product Description',
  'wareWarennummerKN8': 'HS/CN Code',
  'wareRohmasse': 'Gross Weight (kg)',
  'wareEigenmasse': 'Net Weight (kg)',
  'menge': 'Quantity',
  'userid': 'User ID',

  // Goods additional fields
  'wareTarifnummer': 'TARIC Number',
  'wareErsteZusatznummer': 'First Additional Code',
  'wareZweiteZusatznummer': 'Second Additional Code',
  'wareNationaleAngabe': 'National Code',
  'registriernummerextern': 'External Reg. Number',
  'wareVermerk': 'Goods Notes',
  'wareKennungDerSendung': 'Shipment ID',
  'wareGefahrgutnummer': 'Dangerous Goods No.',
  'wareBeforderungZahlung': 'Transport Payment',
  'warecusnummer': 'CUS Number',
  'warePraferenzursprung': 'Preference Origin',
  'wareGesamtpreis': 'Total Price',
  'wareStatistischerwert': 'Statistical Value',
  'wareBewilligungsnummer': 'Authorization Number',

  // Origin and region
  'ursprungsVersendungsregion': 'Region of Origin',
  'mengeinbesondererMabeinheit': 'Quantity in Special Unit',
  'referenznummerUCR': 'UCR Reference',

  // Procedures
  'verfahren': 'Procedure Code',
  'vorangegangenesVerfahren': 'Previous Procedure',
  'zusatzlichesVerfahren': 'Additional Procedure',
  'verfahrenAusfuhrstatt': 'Export Substitute Proc.',

  // Statistics
  'statistischeMenge': 'Statistical Quantity',
  'statistischerWert': 'Statistical Value',

  // Packaging - position-level fields (verpackung array)
  'artderPackstucke': 'Package Type',
  'anzahlderPackstucke': 'Number of Packages',
  'verpackung.anzahlderPackstucke': 'Number of Packages',
  'verpackung.packstuckNummer': 'Package Number',
  'verpackung.artderVerpackung': 'Package Type',
  'verpackung.versandzeichen': 'Shipping Marks',
  'verpackung.versandzeichenTeil2': 'Shipping Mark (Part 2)',
  'verpackung.packstuckverweisPositionsnummer': 'Package Reference Position',

  // Container
  'containerNummer': 'Container Number',

  // Previous documents
  'vorpapiereTyp': 'Prev. Document Type',
  'vorpapiereReferenz': 'Prev. Document Ref.',
  'vorpapiereZusatz': 'Prev. Document Add.',

  // Supporting documents (unterlage)
  'unterlage.art': 'Document Type',
  'unterlage.referenznummer': 'Document Reference',
  'unterlage.datumderausstellung': 'Document Issue Date',
  'unterlage.datumderAusstellung': 'Document Issue Date',
  'unterlage.geltungsdauer': 'Validity Period',
  'unterlage.gultigkeitsdatum': 'Validity Date',
  'unterlage.qualifikator': 'Document Qualifier',
  'unterlage.zusatz': 'Document Addition',
  'unterlage.zusatzlicheAngaben': 'Additional Document Info',
  'unterlage.detail': 'Document Detail',
  'unterlage.masseinheit': 'Unit of Measure',
  'unterlage.mabeinheit': 'Unit of Measure',
  'unterlage.erganzendeMabeinheit': 'Supplementary Unit',
  'unterlage.abschreibungsmenge': 'Deduction Quantity',
  'unterlage.menge': 'Quantity',
  'unterlage.wert': 'Document Value',
  'unterlage.betrag': 'Amount',
  'unterlage.wahrung': 'Currency',
  'unterlage.namederausstellendenBehorde': 'Issuing Authority',
  'unterlage.zeilenPositionsnummer': 'Document Line Position',

  // Other references (sonstigerVerweis)
  'sonstigerVerweis.art': 'Other Reference Type',
  'sonstigerVerweis.referenznummer': 'Other Reference Number',
  'sonstigerVerweis.qualifikator': 'Other Reference Qualifier',
  'sonstigerVerweis.detail': 'Other Reference Detail',
  'sonstigerVerweis.wahrung': 'Currency',
  'sonstigerVerweis.betrag': 'Amount',

  // Export position fields
  'ausfuhrWarenErsteErganzung': 'Export First Add.',
  'ausfuhrWarenZweiteErganzung': 'Export Second Add.',
  'ausfuhrUrsprungsland': 'Export Origin Country',
  'ausfuhrSchatzwert': 'Export Est. Value',
  'ausfuhrMenge': 'Export Quantity',
  'ausfuhrLand': 'Export Country',

  // Recipient at position level
  'empfangerPosition.tin': 'Recipient TIN (Pos)',
  'empfangerPosition.name': 'Recipient Name (Pos)',
  'empfangerPosition.strasse': 'Recipient Street (Pos)',
  'empfangerPosition.ort': 'Recipient City (Pos)',

  // Sequence
  'sequenznummer': 'Sequence Number',

  // Special notes
  'besondereVermerkeAusfuhr': 'Special Export Notes',

  // Position fields (flattened format without position[n] prefix)
  'positionsnummer': 'Position Number',
  'wareMenge': 'Quantity',
  'wareMengeneinheit': 'Unit of Measure',
  'wareRohgewicht': 'Gross Weight',
  'wareUrsprungsland': 'Country of Origin',
  'wareEinzelpreis': 'Unit Price',
};

// Helper to convert camelCase to readable text
const formatFieldName = (name: string): string => {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Type for translation function
type TranslationFn = (key: string, ...args: any[]) => string;

// Helper function to get readable label for any ATLAS path
// When translateFn is provided, uses i18n translations (for German/other languages)
// When not provided, falls back to English labels from ATLAS_PATH_TO_LABEL
export const getFieldLabel = (atlasPath: string, translateFn?: TranslationFn): string => {
  // If translation function is provided, try to get translated label
  if (translateFn) {
    // Try direct field path translation
    const translatedLabel = translateFn(`atlasFieldPaths.${atlasPath}`);
    // Check if translation exists (not returning the key itself)
    if (translatedLabel && !translatedLabel.includes('atlasFieldPaths.')) {
      return translatedLabel;
    }

    // Check for position fields (e.g., position[0].wareWarennummerKN8)
    const positionMatch = atlasPath.match(/^position\[(\d+)\]\.(.+)$/);
    if (positionMatch) {
      const index = parseInt(positionMatch[1], 10);
      const fieldName = positionMatch[2];

      // Try translated position field label
      const translatedFieldLabel = translateFn(`atlasFieldPaths.${fieldName}`);
      const itemSuffix = translateFn('atlasFieldPaths.itemSuffix', index + 1);

      if (translatedFieldLabel && !translatedFieldLabel.includes('atlasFieldPaths.')) {
        return `${translatedFieldLabel} ${itemSuffix}`;
      }
    }
  }

  // Fallback to hardcoded English labels
  // Direct match for header fields
  if (ATLAS_PATH_TO_LABEL[atlasPath]) {
    return ATLAS_PATH_TO_LABEL[atlasPath];
  }

  // Check for position fields (e.g., position[0].wareWarennummerKN8)
  const positionMatch = atlasPath.match(/^position\[(\d+)\]\.(.+)$/);
  if (positionMatch) {
    const index = parseInt(positionMatch[1], 10);
    const fieldName = positionMatch[2];
    const label = POSITION_FIELD_LABELS[fieldName];
    if (label) {
      return `${label} (Item ${index + 1})`;
    }
    // Fallback: format camelCase to readable
    return formatFieldName(fieldName) + ` (Item ${index + 1})`;
  }

  // Check if it's a position field without the position[n] prefix
  if (POSITION_FIELD_LABELS[atlasPath]) {
    return POSITION_FIELD_LABELS[atlasPath];
  }

  // Check the last part of the path against position field labels
  const lastPart = atlasPath.split('.').pop() || atlasPath;
  if (POSITION_FIELD_LABELS[lastPart]) {
    return POSITION_FIELD_LABELS[lastPart];
  }

  // Fallback: format the last part of the path
  return formatFieldName(lastPart);
};

// Hook for using getFieldLabel with translations in React components
export const useFieldLabel = () => {
  const { tString } = useTranslation();
  return (atlasPath: string) => getFieldLabel(atlasPath, tString);
};

const getNestedValue = (obj: any, path: string): any => {
  // Handle array notation like position[0].ware.menge
  const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
  return keys.reduce((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    return acc[key];
  }, obj);
};

const AtlasValidationPreview: React.FC<AtlasValidationPreviewProps> = ({
  extractedData,
  selectedModule,
}) => {
  const { t, tString } = useTranslation();
  const [isValidating, setIsValidating] = useState(false);
  const isValidatingRef = useRef(false);
  const [validation, setValidation] = useState<{
    complete: AtlasField[];
    missing: AtlasField[];
    warnings: ValidationIssue[];
    completenessPercent: number;
  }>({
    complete: [],
    missing: [],
    warnings: [],
    completenessPercent: 0,
  });

  // Required documents with translations
  const requiredDocuments = [
    tString('documentProcessing.atlasValidation.documents.commercialInvoice'),
    tString('documentProcessing.atlasValidation.documents.packingList'),
    tString('documentProcessing.atlasValidation.documents.certificateOfOrigin'),
    tString('documentProcessing.atlasValidation.documents.exportLicense'),
  ];

  // Fetch validation from backend
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const validateAtlasData = async () => {
      // Prevent concurrent validations
      if (isValidatingRef.current || isValidating) {
        return;
      }

      isValidatingRef.current = true;
      setIsValidating(true);
      try {
        // Call backend validation API
        const response = await validationService.validateDocument(
          extractedData,
          selectedModule
        );

        // Convert backend response to component format
        const complete: AtlasField[] = [];
        const missing: AtlasField[] = [];
        const warnings: ValidationIssue[] = [];

        // Process required fields from response
        response.required_fields.forEach(field => {
          const atlasField: AtlasField = {
            path: field.field_path,
            label: field.field_name,
            required: true,
            section: field.section as 'kopf' | 'ausfuhrer' | 'empfaenger' | 'position',
          };

          if (field.is_present) {
            complete.push(atlasField);
          } else {
            missing.push(atlasField);
          }
        });

        // Add warnings from response
        response.warnings.forEach(warning => {
          // Extract field name from warning if possible
          const match = warning.match(/Please verify: (.+) =/);
          const fieldName = match ? match[1] : 'Field';
          warnings.push({
            type: 'warning',
            field: fieldName,
            message: warning,
          });
        });

        setValidation({
          complete,
          missing,
          warnings,
          completenessPercent: response.completeness_percent,
        });
      } catch (error) {
        console.error('Failed to validate document:', error);
        // Fallback to local validation if API fails
        const atlasFields = getATLASFields(extractedData);
        const complete: AtlasField[] = [];
        const missing: AtlasField[] = [];
        const warnings: ValidationIssue[] = [];

        atlasFields.forEach((field) => {
          const value = getNestedValue(extractedData, field.path);

          if (value !== null && value !== undefined && value !== '') {
            complete.push(field);

            // Add warnings for fields that should be verified
            if (field.section === 'empfaenger') {
              warnings.push({
                type: 'warning',
                field: field.label,
                message: tString('documentProcessing.atlasValidation.verifyRecipient'),
              });
            }
            if (field.path.includes('wareTarifnummer')) {
              warnings.push({
                type: 'warning',
                field: field.label,
                message: tString('documentProcessing.atlasValidation.verifyHsCode'),
              });
            }
          } else if (field.required) {
            missing.push(field);
          }
        });

        const completenessPercent = Math.round((complete.length / atlasFields.length) * 100);
        setValidation({ complete, missing, warnings, completenessPercent });
      } finally {
        setIsValidating(false);
        isValidatingRef.current = false;
      }
    };

    // Validate when data changes with debouncing
    if (!extractedData || !selectedModule) {
      return;
    }

    // Debounce validation calls to prevent rapid re-execution
    const timer = setTimeout(() => {
      validateAtlasData();
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [extractedData, selectedModule]);

  // Only show for ATLAS module
  if (!selectedModule || !selectedModule.toLowerCase().includes('atlas')) {
    return null;
  }

  const canSubmit = validation.missing.length === 0;

  const groupBySection = (fields: AtlasField[]) => {
    return fields.reduce((acc, field) => {
      if (!acc[field.section]) {
        acc[field.section] = [];
      }
      acc[field.section].push(field);
      return acc;
    }, {} as Record<string, AtlasField[]>);
  };

  const missingBySection = groupBySection(validation.missing);
  const completeBySection = groupBySection(validation.complete);

  // Helper to get translated field label
  const getTranslatedFieldLabel = (field: AtlasField) => {
    return getFieldLabel(field.path, tString);
  };

  const sectionNames: Record<string, string> = {
    kopf: tString('documentProcessing.atlasValidation.sections.header'),
    ausfuhrer: tString('documentProcessing.atlasValidation.sections.exporter'),
    empfaenger: tString('documentProcessing.atlasValidation.sections.recipient'),
    anmelder: tString('documentProcessing.atlasValidation.sections.declarant'),
    lieferung: tString('documentProcessing.atlasValidation.sections.delivery'),
    sendung: tString('documentProcessing.atlasValidation.sections.shipment'),
    position: tString('documentProcessing.atlasValidation.sections.lineItems'),
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 2, mb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" fontWeight={600}>
          {tString('documentProcessing.atlasValidation.title')}
        </Typography>
        {isValidating ? (
          <CircularProgress size={24} />
        ) : (
          <Chip
            label={tString('documentProcessing.atlasValidation.percentComplete', validation.completenessPercent)}
            size="medium"
            sx={{
              backgroundColor: validation.completenessPercent === 100 ? brand.success : brand.warning,
              color: validation.completenessPercent === 100 ? brand.successText : brand.warningText,
              borderRadius: radii.pill,
              fontSize: 12,
              fontWeight: 700,
              px: 1,
              py: 0.25,
            }}
          />
        )}
      </Box>

      <Grid container spacing={2}>
        {/* Completeness Status */}
        <Grid item xs={12}>
          {canSubmit ? (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              {tString('documentProcessing.atlasValidation.allFieldsComplete')}
            </Alert>
          ) : (
            <Alert severity="error" icon={<ErrorIcon />}>
              {tString('documentProcessing.atlasValidation.missingFields', validation.missing.length)}
            </Alert>
          )}
        </Grid>

        {/* Missing Fields by Section */}
        {validation.missing.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={600} color="error" gutterBottom>
              {tString('documentProcessing.atlasValidation.missingRequiredFields')}
            </Typography>
            {Object.entries(missingBySection).map(([section, fields]) => (
              <Box key={section} mb={2}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {sectionNames[section]}
                </Typography>
                <List dense>
                  {fields.map((field) => (
                    <ListItem key={field.path}>
                      <ListItemIcon>
                        <ErrorIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={getTranslatedFieldLabel(field)}
                        secondary={`Field: ${field.path}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Grid>
        )}

        {/* Complete Fields Summary */}
        {validation.complete.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={600} color="success" gutterBottom>
              {tString('documentProcessing.atlasValidation.completeFields', validation.complete.length)}
            </Typography>
            {Object.entries(completeBySection).map(([section, fields]) => (
              <Box key={section} mb={2}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {sectionNames[section]}
                </Typography>
                <List dense>
                  {fields.slice(0, 3).map((field) => (
                    <ListItem key={field.path}>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={getTranslatedFieldLabel(field)}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                  {fields.length > 3 && (
                    <ListItem>
                      <ListItemText
                        primary={tString('documentProcessing.atlasValidation.andMore', fields.length - 3)}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            ))}
          </Grid>
        )}

        {/* Warnings */}
        {validation.warnings.length > 0 && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} color="warning" gutterBottom>
              {tString('documentProcessing.atlasValidation.pleaseVerify')}
            </Typography>
            <List dense>
              {Array.from(new Set(validation.warnings.map(w => w.message))).map((message, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <WarningIcon color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={message}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        )}

        {/* Required Documents */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {tString('documentProcessing.atlasValidation.requiredDocuments')}
          </Typography>
          <List dense>
            {requiredDocuments.map((doc, idx) => (
              <ListItem key={idx}>
                <ListItemIcon>
                  <DescriptionIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={doc}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AtlasValidationPreview;
